import React, { useState, useEffect, useMemo } from 'react';
import { Lock, Shield } from 'lucide-react';
import useAppStore from '../store/appStore';
import useAdminStore from '../store/adminStore';
import { getAppPages, AppPageConfig, AppIconMap } from '../config/pageRegistry';
import { auth } from '../config/firebase';
import PagePinLockModal from './security/PagePinLockModal';
import {
  isPagePinLockedForUi,
  normalizePinLockConfig,
  PIN_SESSION_EVENT,
} from '../utils/pinLockSession';

// --- ICONS FÜR MOBILE STEUERUNG ---
const MenuIcon = ({ size = 24, className = "" }: any) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

const XIcon = ({ size = 24, className = "" }: any) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

interface SmartSidebarProps {
  currentPage: string;
  onNavigate: (pageKey: string) => void;
  /** Muss von App kommen — sonst bleibt die Sidebar auf veraltetem Jahresstatus hängen */
  currentYearStatus?: string;
  currentYear?: string;
}

const SmartSidebar: React.FC<SmartSidebarProps> = ({
  currentPage,
  onNavigate,
  currentYearStatus = '',
  currentYear = '',
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pinSessionTick, setPinSessionTick] = useState(0);
  
  const { clientData, settings, unlockedPremiumTools, openPremiumModal } = useAppStore(state => ({
    clientData: state.clientData,
    settings: state.settings,
    unlockedPremiumTools: state.unlockedPremiumTools,
    openPremiumModal: state.openPremiumModal,
  }));

  const impersonatedClientId = useAdminStore((s) => s.impersonatedClientId);
  const isGodMode =
    !!impersonatedClientId ||
    !!sessionStorage.getItem('GOD_MODE_ID') ||
    !!(clientData as any)?._isGodMode;

  const mandantUid = impersonatedClientId || auth.currentUser?.uid || '';
  const pinLockConfig = normalizePinLockConfig((clientData as any)?.pinLockConfig);

  useEffect(() => {
    const onChange = () => setPinSessionTick((t) => t + 1);
    window.addEventListener(PIN_SESSION_EVENT, onChange);
    return () => window.removeEventListener(PIN_SESSION_EVENT, onChange);
  }, []);

  /** Store + Akte mergen — sonst fehlt z. B. pe-plan im Menü trotz Aktivierung */
  const effectiveUnlockedPremiumTools = useMemo(() => {
    const out: string[] = [];
    for (const list of [unlockedPremiumTools, clientData?.unlockedPremiumTools]) {
      if (!Array.isArray(list)) continue;
      for (const id of list) {
        if (typeof id === 'string' && id && !out.includes(id)) out.push(id);
      }
    }
    return out;
  }, [unlockedPremiumTools, clientData?.unlockedPremiumTools]);

  const pages = useMemo(() => {
    const enriched = clientData
      ? {
          ...clientData,
          _forcedYearStatus: currentYearStatus || undefined,
          _isGodMode: isGodMode,
          unlockedPremiumTools: effectiveUnlockedPremiumTools,
        }
      : clientData;
    return getAppPages(enriched, settings, onNavigate) || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientData, settings, onNavigate, currentPage, currentYearStatus, currentYear, isGodMode, effectiveUnlockedPremiumTools]);

  const [isInYearContext, setIsInYearContext] = useState(false);

  useEffect(() => {
      const activePageConfig = pages.find(p => p.key === currentPage);
      if (activePageConfig) {
          if (activePageConfig.group === 'verwaltung') {
              setIsInYearContext(false);
          } else {
              setIsInYearContext(true);
          }
      }
  }, [currentPage, pages]);

  // Status aus Akte schlägt Prop — falls Dev-Phase LocalStorage nicht getroffen hat
  const statusFromAkte = (() => {
    const y = currentYear || (typeof window !== 'undefined' ? localStorage.getItem('CURRENT_YEAR') : null);
    if (!y || !clientData?.geschaeftsjahre) return '';
    const hit = clientData.geschaeftsjahre.find((j: any) => String(j.jahr) === String(y));
    return hit?.status ? String(hit.status) : '';
  })();
  const isDatenerfassungMode =
    statusFromAkte === 'datenerfassung' ||
    statusFromAkte === 'angefragt' ||
    currentYearStatus === 'datenerfassung' ||
    currentYearStatus === 'angefragt';

  const groups = useMemo(() => {
    const isFoyer = !isInYearContext;

    const filterVisible = (pageList: AppPageConfig[]) =>
      pageList.filter((p) => {
        if (!p.isVisible) return false;
        if (
          (p.key === 'pe-papers' ||
            p.key === 'pe-profit' ||
            p.key === 'pe-start' ||
            p.key === 'pe-rent' ||
            p.key === 'pe-plan' ||
            p.key === 'pe-scout' ||
            p.key === 'pe-facts') &&
          !effectiveUnlockedPremiumTools.includes(p.key)
        ) {
          return false;
        }
        // Intern-Tools: nur Gott-Modus (nie Mandantenmenü)
        if ((p.key === 'planungsrechner' || p.key === 'pe-solo') && !isGodMode) {
          return false;
        }
        // Datenerfassung: Sichtbarkeit kommt aus getAppPages (Whitelist + Admin-Toggles)
        if (isDatenerfassungMode && p.key === 'coach-akte') {
          return isGodMode;
        }
        return true;
      });

    // Premium-Tools in Verwaltung (z. B. PEPapers) immer ans Ende der Gruppe
    const verwaltungPages = filterVisible(pages.filter((p) => p.group === 'verwaltung')).sort(
      (a, b) => Number(!!a.isPremium) - Number(!!b.isPremium)
    );

    return {
      verwaltung: verwaltungPages,
      basis: isFoyer ? [] : filterVisible(pages.filter((p) => p.group === 'basis')),
      organisation: isFoyer ? [] : filterVisible(pages.filter((p) => p.group === 'organisation')),
      daten: isFoyer ? [] : filterVisible(pages.filter((p) => p.group === 'daten')),
      tools: isFoyer ? [] : filterVisible(pages.filter((p) => p.group === 'tools')),
      auswertung: isFoyer ? [] : filterVisible(pages.filter((p) => p.group === 'auswertung')),
      analyse: isFoyer ? [] : filterVisible(pages.filter((p) => p.group === 'analyse')),
      abschluss: isFoyer ? [] : filterVisible(pages.filter((p) => p.group === 'abschluss')),
    };
  }, [pages, isInYearContext, effectiveUnlockedPremiumTools, isDatenerfassungMode, isGodMode]);

  const showLabels = isHovered || isMobileOpen;

  const PremiumWatermark = ({ className = '' }: { className?: string }) => (
    <span
      aria-hidden
      className={`pointer-events-none select-none font-black uppercase tracking-[0.2em] leading-none opacity-30 ${className}`}
      style={{
        background: 'linear-gradient(135deg, #ef4444 0%, #E6C299 45%, #b48a56 100%)',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        color: 'transparent',
      }}
    >
      Premium
    </span>
  );

  const renderLink = (p: AppPageConfig) => {
    const Icon = AppIconMap[p.iconStr] || AppIconMap.help;
    const isActive = currentPage === p.key;
    const isPremiumPage = p.isPremium === true;
    const showPinLock =
      pinSessionTick >= 0 &&
      isPagePinLockedForUi(p.key, pinLockConfig, mandantUid, isGodMode);
    const premiumGlassStyle =
      'backdrop-blur-md bg-[#E6C299]/10 border border-[#E6C299]/20 text-pe-text shadow-[0_0_15px_rgba(230,194,153,0.03)]';

    return (
      <button
        key={p.key}
        onClick={() => {
          onNavigate(p.key);
          setIsMobileOpen(false);
        }}
        className={`
          group relative flex items-center w-full h-11 px-0 my-1
          transition-all duration-200 overflow-hidden
          ${isPremiumPage
            ? `mx-2 rounded-lg ${premiumGlassStyle} ${isActive ? 'ring-1 ring-[#E6C299]/50' : ''}`
            : isActive
              ? 'bg-[#E6C299]/20 dark:bg-white/10 text-pe-text'
              : 'text-pe-muted hover:bg-pe-hover hover:text-pe-text'}
        `}
        title={!showLabels ? p.label : ''}
      >
        <div className={`w-4 h-full absolute left-0 ${p.color} shadow-[0_0_10px_rgba(0,0,0,0.5)]`}></div>

        <div className={`flex-shrink-0 flex items-center justify-center w-12 h-full ml-4 transition-colors duration-200 relative z-10 ${isActive ? 'text-pe-accent-text' : (p.iconClass || 'text-pe-muted')}`}>
          <Icon size={20} strokeWidth={isActive ? 2.5 : 2} />
        </div>

        {showLabels && isPremiumPage && (
          <PremiumWatermark className="absolute right-4 top-1/2 -translate-y-1/2 text-xs z-0" />
        )}

        <div className={`
          relative z-10 whitespace-nowrap text-sm font-medium tracking-wide transition-opacity duration-300 flex items-center gap-1.5
          ${showLabels ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}
          ${isActive ? 'text-pe-accent-text' : ''}
        `}>
          {p.label}
          {showPinLock && (
            <Lock className="w-3.5 h-3.5 text-amber-800 dark:text-amber-400/80 flex-shrink-0" strokeWidth={2.5} aria-hidden />
          )}
        </div>
        {!showLabels && showPinLock && (
          <Lock className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-amber-800 dark:text-amber-400/80 z-10" strokeWidth={2.5} aria-hidden />
        )}
      </button>
    );
  };

  const renderPremiumLink = () => (
    <button
      type="button"
      onClick={() => {
        openPremiumModal();
        setIsMobileOpen(false);
      }}
      className="group relative flex items-center w-full h-11 px-0 my-1 mx-2 rounded-lg transition-all duration-200 overflow-hidden backdrop-blur-md bg-[#E6C299]/10 border border-[#E6C299]/20 text-pe-text shadow-[0_0_15px_rgba(230,194,153,0.03)]"
      title={!showLabels ? 'Premium-Tools' : ''}
    >
      <div className="w-4 h-full absolute left-0 bg-gradient-to-b from-[#E6C299] to-[#b48a56] shadow-[0_0_10px_rgba(230,194,153,0.4)]" />
      <div className="flex-shrink-0 flex items-center justify-center w-12 h-full ml-4 text-[#E6C299] relative z-10">
        <span className="text-base leading-none">🌟</span>
      </div>
      {showLabels && (
        <PremiumWatermark className="absolute right-4 top-1/2 -translate-y-1/2 text-xs z-0" />
      )}
      <div
        className={`relative z-10 whitespace-nowrap text-sm font-bold tracking-wide transition-opacity duration-300 ${
          showLabels ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'
        }`}
      >
        Premium-Tools
      </div>
    </button>
  );

  return (
    <>
      {/* MOBILE HAMBURGER — unter God-Mode-Leiste, sonst oben links */}
      {!isMobileOpen && (
        <button
          onClick={() => setIsMobileOpen(true)}
          className={`md:hidden fixed left-4 z-[100] bg-pe-elevated dark:bg-[#15171e] hover:bg-pe-hover dark:hover:bg-[#1a1d24] text-pe-accent-text border border-[#E6C299]/30 p-2.5 rounded-xl shadow-[0_0_15px_rgba(230,194,153,0.15)] flex items-center justify-center transition-all ${
            isGodMode ? 'top-12' : 'top-4'
          }`}
          aria-label="Menü öffnen"
        >
          <MenuIcon size={24} />
        </button>
      )}

      {/* MOBILE BACKDROP - Klick daneben schließt das Menü */}
      {isMobileOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/80 backdrop-blur-sm z-[105]"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      <aside 
        className={`
          fixed left-0 top-0 h-screen z-[110] bg-pe-elevated dark:bg-[#030303] border-r border-pe-border shadow-2xl
          transition-all duration-300 ease-in-out flex flex-col
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'} 
          md:translate-x-0 
          w-72 
          ${isHovered ? 'md:w-72' : 'md:w-16'}
        `}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="h-20 flex-shrink-0 flex items-center justify-center border-b border-pe-border bg-pe-elevated z-10 relative">
           <div className={`font-bold text-pe-accent-text text-xl transition-all ${showLabels ? 'scale-100' : 'scale-75'}`}>
              {showLabels ? 'PreisExpert' : 'PE'}
           </div>
           
           {/* MOBILE CLOSE BUTTON */}
           {isMobileOpen && (
             <button 
               onClick={() => setIsMobileOpen(false)} 
               className="md:hidden absolute right-4 p-2 text-pe-muted dark:text-slate-400 hover:text-pe-text transition-colors"
             >
                <XIcon size={20} />
             </button>
           )}
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden py-4 custom-scrollbar">

          <div className="mb-6">
            {showLabels && (
              <div className="text-xs uppercase text-pe-accent-text font-medium px-4 mb-2 tracking-widest border-b border-[#E6C299]/20 pb-1 mx-2">
                Premium
              </div>
            )}
            {!showLabels && <div className="h-px bg-[#E6C299]/20 mx-3 my-2" />}
            {renderPremiumLink()}
          </div>

          {groups.verwaltung.length > 0 && (
            <div className="mb-6">
              {showLabels && <div className="text-xs uppercase text-purple-700 dark:text-purple-400 font-medium px-4 mb-2 tracking-widest border-b border-purple-900/30 pb-1 mx-2">Verwaltung & Mandat</div>}
              {!showLabels && <div className="h-px bg-purple-900/30 mx-3 my-2"></div>}
              {groups.verwaltung.map(renderLink)}
            </div>
          )}

          {groups.basis.length > 0 && (
            <div className="mb-6">
              {showLabels && <div className="text-xs uppercase text-pe-muted font-medium px-4 mb-2 tracking-widest border-b border-slate-900/30 pb-1 mx-2">Grundlagen</div>}
              {!showLabels && <div className="h-px bg-slate-900/30 mx-3 my-2"></div>}
              {groups.basis.map(renderLink)}
            </div>
          )}

          {groups.organisation.length > 0 && (
            <div className="mb-6">
              {showLabels && <div className="text-xs uppercase text-sky-700 dark:text-sky-400 font-medium px-4 mb-2 tracking-widest border-b border-sky-900/30 pb-1 mx-2">Organisation</div>}
              {!showLabels && <div className="h-px bg-sky-900/30 mx-3 my-2"></div>}
              {groups.organisation.map(renderLink)}
            </div>
          )}

          {groups.daten.length > 0 && (
            <div className="mb-6">
              {showLabels && <div className="text-xs uppercase text-blue-700 dark:text-blue-500 font-medium px-4 mb-2 tracking-widest border-b border-blue-900/30 pb-1 mx-2">Arbeitsdaten</div>}
              {!showLabels && <div className="h-px bg-blue-900/30 mx-3 my-2"></div>}
              {groups.daten.map(renderLink)}
            </div>
          )}

          {groups.tools.length > 0 && (
            <div className="mb-6">
              {showLabels && <div className="text-xs uppercase text-emerald-700 dark:text-emerald-500 font-medium px-4 mb-2 tracking-widest border-b border-emerald-900/30 pb-1 mx-2">Arbeitstools</div>}
              {!showLabels && <div className="h-px bg-emerald-900/30 mx-3 my-2"></div>}
              {groups.tools.map(renderLink)}
            </div>
          )}

          {groups.auswertung.length > 0 && (
            <div className="mb-6">
              {showLabels && <div className="text-xs uppercase text-[#9C544D] dark:text-[#CC8A84] font-medium px-4 mb-2 tracking-widest border-b border-[#CC8A84]/30 pb-1 mx-2">Auswertung</div>}
              {!showLabels && <div className="h-px bg-[#CC8A84]/30 mx-3 my-2"></div>}
              {groups.auswertung.map(renderLink)}
            </div>
          )}

          {groups.analyse.length > 0 && (
            <div className="mb-6">
               {showLabels && <div className="text-xs uppercase text-pe-accent-text font-medium px-4 mb-2 tracking-widest border-b border-[#E6C299]/30 pb-1 mx-2">Analyse Tools</div>}
               {!showLabels && <div className="h-px bg-[#E6C299]/30 mx-3 my-2"></div>}
               {groups.analyse.map(renderLink)}
            </div>
          )}

          {groups.abschluss.length > 0 && (
            <div className="mb-20"> 
              {showLabels && <div className="text-xs uppercase text-pe-accent-text font-medium px-4 mb-2 tracking-widest border-b border-[#E6C299]/30 pb-1 mx-2">Abschluss</div>}
              {!showLabels && <div className="h-px bg-[#E6C299]/30 mx-3 my-2"></div>}
              {groups.abschluss.map(renderLink)}
            </div>
          )}
        </div>
        
        <div className="flex-shrink-0 border-t border-pe-border bg-pe-elevated">
          <button
            type="button"
            onClick={() => setPinModalOpen(true)}
            className="w-full h-10 flex items-center justify-center gap-2 text-pe-muted hover:text-pe-accent-text hover:bg-pe-hover transition-colors text-xs"
            title="Master-PIN / Seitensperre"
          >
            <Shield className="w-4 h-4 text-amber-800 dark:text-amber-400" strokeWidth={2.25} />
            {showLabels && (
              <span className="font-medium tracking-wide">
                {pinLockConfig.isPinActive ? 'Master-PIN' : 'PIN einrichten'}
              </span>
            )}
          </button>
          <div className="h-8 flex items-center justify-center text-pe-muted text-[10px] border-t border-pe-border/60">
            {showLabels ? 'v18.0 Elite' : 'v18'}
          </div>
        </div>
      </aside>

      <PagePinLockModal isOpen={pinModalOpen} onClose={() => setPinModalOpen(false)} />
    </>
  );
};

export default SmartSidebar;